import { Button } from "@/components/common/Button/Button"
import { Logo } from "@/components/common/Logo/Logo"
import { Layout } from "@/components/layout"
import { isMcOnlyGroup } from "@/components/layout/sidebar-v2/resolveSidebarNav"
import { useGetAllFeatureFlags } from "@/contexts/FeatureFlagContext"
import {
  AribaOnboarding,
  getReferralSource,
  isAribaOnboardingDismissed,
} from "@/features/ariba-onboarding"
import {
  INITIAL_ONBOARDING_TOUR,
  TourWelcomeModal,
  useOnboarding,
} from "@/features/onboarding"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { getIsUserOnboarded, getOnboardingStep } from "@/utils/onboarding"
import { useAuthenticator } from "@aws-amplify/ui-react"
import { IconDatabase, IconFileUpload } from "@tabler/icons-react"
import { useEffect, useState } from "react"

import { toast } from "@/components/common/Toast"

export const HomeScreen: React.FC = () => {
  const { translate } = useTranslation()
  const { authStatus } = useAuthenticator((context) => [context.authStatus])
  const [isOnboarded, setIsOnboarded] = useState<boolean | null>(null)
  const [showTourModalLocal, setShowTourModalLocal] = useState<boolean>(false)
  const { isActive, showWelcomeModal, closeWelcomeModal } = useOnboarding()
  const flags = useGetAllFeatureFlags()
  const onboardingTourEnabled = flags.ONBOARDING_TOUR
  // Suppliers from the SAP Ariba landing page get their own checklist instead
  // of the questionnaire tour, also after they closed it.
  const [isAribaReferral] = useState(() => getReferralSource() === "ariba")
  const [showAribaOnboarding, setShowAribaOnboarding] = useState(
    () => isAribaReferral && !isAribaOnboardingDismissed(),
  )
  // The tour must not auto-fire when ONBOARDING_TOUR is off, for an Ariba
  // referral (above), or when the org is MC-only (G2), where the sidebar has no
  // navItem1 so the questionnaire tour's first step can never find its target.
  // An MC-specific tour is a separate, not-yet-built effort.
  const suppressQuestionnaireTour =
    !onboardingTourEnabled || isMcOnlyGroup(flags) || isAribaReferral

  const showTourModal = showTourModalLocal || showWelcomeModal

  useEffect(() => {
    if (authStatus !== "authenticated") return
    const checkOnboardingStatus = async () => {
      try {
        const isUserOnboarded = await getIsUserOnboarded()
        const onboardingStep = await getOnboardingStep()

        setIsOnboarded(isUserOnboarded)

        if (
          !isUserOnboarded &&
          onboardingStep === null &&
          !suppressQuestionnaireTour
        ) {
          setShowTourModalLocal(true)
        }
      } catch {
        toast.error(translate("USER_DATA_ERROR"))
      }
    }

    checkOnboardingStatus()
  }, [authStatus, suppressQuestionnaireTour])

  useEffect(() => {
    if (isActive) {
      setShowTourModalLocal(false)
      closeWelcomeModal()
    }
  }, [isActive, closeWelcomeModal])

  if (showAribaOnboarding) {
    return (
      <Layout>
        <div className="h-full overflow-y-auto">
          <AribaOnboarding onClose={() => setShowAribaOnboarding(false)} />
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="flex h-full flex-col items-center justify-center gap-24">
        <div className="h-12">
          <Logo path="/" />
        </div>

        <div className="flex justify-center gap-5">
          <Button
            path="/questionnaire/upload"
            variantColor="yellow"
            title={translate("HOME/BTN_UPLOAD")}
            label={translate("HOME/BTN_UPLOAD")}
            IconLeft={IconFileUpload}
          />
          <Button
            path="/knowledgebase"
            variantColor="dark"
            title={translate("HOME/BTN_DATABASE")}
            label={translate("HOME/BTN_DATABASE")}
            IconLeft={IconDatabase}
          />
        </div>
      </div>

      {isOnboarded === false && <></>}

      {onboardingTourEnabled ? (
        <TourWelcomeModal
          isOpen={showTourModal}
          tourId={INITIAL_ONBOARDING_TOUR.id}
          title={translate("TOUR/WELCOME/TITLE")}
          description={translate("TOUR/WELCOME/DESCRIPTION")}
          onClose={() => {
            setShowTourModalLocal(false)
            closeWelcomeModal()
          }}
        />
      ) : null}
    </Layout>
  )
}
